<template>
  <div class="odd:pr-5">
    <div
      class="flex justify-between space-x-4 rounded p-2"
      :class="{ 'hover:bg-grey-hover': type === 'phone' }"
    >
      <div class="flex items-center justify-start space-x-4">
        <icon-base
          :icon="iconDetails.icon"
          width="16"
          height="16"
          :view-box="iconDetails.viewBox"
          :class="iconDetails.class"
        />
        <div>
          <info-span-copy
            :text="valueAsString"
            :disabled="!clickToCopy"
            :data-cy="id"
          ></info-span-copy>
        </div>
        <block-phone-actions
          v-if="type === 'phone' && value && value !== '-'"
          class="mt-[10px]"
          :telephone-info="telephoneStatusInfo"
          :contact-name="phoneContactName"
          :is-business="isBusiness"
        />
        <div
          v-if="is_primary"
          class="pointer-events-none h-5 w-5 rounded-full bg-primary-background-active text-center text-xs font-semibold leading-5 text-primary"
        >
          P
        </div>
      </div>
      <div>
        <tooltip
          v-if="!loadingSources && sourcesForField"
          ref="sourcesTooltip"
          placement="right"
          :arrow="false"
          sticky="reference"
          theme="none"
          interactive
          :popper-options="popperOptions"
        >
          <icon-base
            :icon="IconSourceDetails"
            data-cy="sources-tooltip"
            class="cursor-pointer"
          />
          <template #content>
            <field-sources-component :sources="sourcesForField.sources" />
          </template>
        </tooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type Component } from "vue";
import type { IOwner } from "@/models/users";
import type { IRootState } from "@/models/state";
import type { TippyComponent } from "vue-tippy";
import type { FieldSource } from "@/models/applications";
import { TelephoneStatus } from "@/enums/communicationLogs";
import { useDealsBase } from "@/hooks/deals";
import { useDealTelephones } from "@/hooks/deals";
import { useStore } from "vuex";
import { formatUserFullName } from "@/helpers/formatting";
import InfoSpanCopy from "@/views/deals/components/InfoSpanCopy.vue";
import BlockPhoneActions from "@/views/deals/components/BlockPhoneActions.vue";
import FieldSourcesComponent from "@/views/deals/components/fieldSources/FieldSources.vue";
import IconAddress from "@/components/icons/IconAddress.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconBadNumber from "@/components/icons/IconBadNumber.vue";
import IconDoNotContact from "@/components/icons/IconDoNotContact.vue";
import IconSourceDetails from "@/components/icons/IconSourceDetails.vue";

type FieldData = {
  id: string;
  value: string;
  is_primary?: boolean;
};

type IconDetails = {
  icon: Component;
  class?: string;
  viewBox?: string;
};

const {
  type = "address",
  data,
  isBusiness
} = defineProps<{
  type?: "address" | "phone";
  clickToCopy?: boolean;
  data: FieldData;
  isBusiness?: boolean;
  loadingSources?: boolean;
}>();

const { id, value, is_primary } = data;

const { getters } = useStore<IRootState>();
const { activeDeal } = useDealsBase();
const { getTelephoneStatusInfo } = useDealTelephones(activeDeal);

const fieldSources = computed<Record<string, FieldSource[]>>(
  () => getters["applications/field_sources"]
);

const sourcesTooltip = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["bottom", "top"] }
    }
  ]
});

const telephoneStatusInfo = computed(() =>
  getTelephoneStatusInfo(value?.toString(), isBusiness)
);

const valueAsString = computed(() =>
  Array.isArray(value) ? value.join(", ") : value
);

const isOwnerPhone = (owner: IOwner | null) =>
  !!owner?.all_telephones
    ?.map(({ number }) => number)
    .includes(value?.toString());

const phoneContactName = computed(() => {
  if (isBusiness) {
    return activeDeal.value?.business?.business_legal_name || "";
  }

  const isMainOwnerPhone = isOwnerPhone(activeDeal.value.personal_information);

  if (isMainOwnerPhone) {
    return formatUserFullName(activeDeal.value.personal_information);
  }

  const ownerWithPhone =
    activeDeal.value?.business?.other_owners?.find(isOwnerPhone) || null;

  return formatUserFullName(ownerWithPhone);
});

const iconDetails = computed<IconDetails>(() => {
  if (type === "address") {
    return { icon: IconAddress };
  }
  const options = {
    [TelephoneStatus.DoNotContact]: {
      icon: IconDoNotContact,
      class: "text-error"
    },
    [TelephoneStatus.BadNumber]: {
      icon: IconBadNumber,
      class: "text-warning",
      viewBox: "1 2 10 10"
    }
  };
  return telephoneStatusInfo.value?.status
    ? options[telephoneStatusInfo.value?.status]
    : { icon: IconTelephone };
});

const sourcesForField = computed(() => {
  const sourceKey = type === "phone" ? "phone_number" : undefined;
  if (!sourceKey || !Array.isArray(fieldSources.value?.[sourceKey])) {
    return undefined;
  }
  return fieldSources.value?.[sourceKey]?.find((source) => {
    return source.value === data.value;
  });
});
</script>
